import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input, Button, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  iconRetinaUrl: iconRetina,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const DEFAULT_CENTER = [25.0330, 121.5654];
const DEFAULT_ZOOM = 13;

function parseCoord(latStr, lngStr) {
  const lat = parseFloat((latStr || '').trim());
  const lng = parseFloat((lngStr || '').trim());
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

/** 點擊地圖選點 */
function MapClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/** 外部在輸入框修改經緯度時，地圖跟隨 */
function FlyToCoordinates({ latStr, lngStr }) {
  const map = useMap();
  useEffect(() => {
    const p = parseCoord(latStr, lngStr);
    if (!p) return;
    map.flyTo([p.lat, p.lng], Math.max(map.getZoom(), 15), { duration: 0.35 });
  }, [latStr, lngStr, map]);
  return null;
}

/** Modal 內開啟時修正灰階圖磚尺寸 */
function InvalidateOnMount() {
  const map = useMap();
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      map.invalidateSize();
      setTimeout(() => map.invalidateSize(), 300);
    });
    return () => cancelAnimationFrame(id);
  }, [map]);
  return null;
}

/**
 * 免費地圖選點：OpenStreetMap 圖層 + Photon 搜尋（無需 API Key）
 */
export default function MetaTemplateLocationPicker({ latitude, longitude, onChange, t }) {
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');

  const parsed = useMemo(() => parseCoord(latitude, longitude), [latitude, longitude]);

  const center = parsed ? [parsed.lat, parsed.lng] : DEFAULT_CENTER;
  const zoom = parsed ? 16 : DEFAULT_ZOOM;

  const handlePick = useCallback(
    (lat, lng) => {
      onChange({
        latitude: String(lat),
        longitude: String(lng),
      });
    },
    [onChange]
  );

  const handleSearch = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=1`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Photon error');
      const data = await res.json();
      const feat = data.features?.[0];
      if (!feat?.geometry?.coordinates) {
        return;
      }
      const [lon, lat] = feat.geometry.coordinates;
      const props = feat.properties || {};
      const name =
        props.name ||
        props.street ||
        [props.city, props.country].filter(Boolean).join(' ') ||
        '';
      const addressParts = [props.housenumber, props.street, props.postcode, props.city, props.state, props.country].filter(
        Boolean
      );
      const address = addressParts.join(', ');
      onChange({
        latitude: String(lat),
        longitude: String(lon),
        ...(name ? { name } : {}),
        ...(address ? { address } : {}),
      });
    } catch (e) {
      console.warn('Photon search failed', e);
    } finally {
      setSearching(false);
    }
  }, [query, onChange]);

  return (
    <div style={{ marginBottom: 16 }}>
      <Space.Compact style={{ width: '100%', marginBottom: 10 }}>
        <Input
          placeholder={t('whatsappTemplate.metaTemplate.locationSearchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
        />
        <Button type="primary" icon={<SearchOutlined />} loading={searching} onClick={handleSearch}>
          {t('whatsappTemplate.metaTemplate.locationSearch')}
        </Button>
      </Space.Compact>
      <div style={{ fontSize: '12px', color: '#888', marginBottom: 8 }}>
        {t('whatsappTemplate.metaTemplate.locationMapHint')}
      </div>
      <div
        style={{
          height: 280,
          width: '100%',
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #d9d9d9',
        }}
      >
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
          attributionControl
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <InvalidateOnMount />
          <MapClickHandler onPick={handlePick} />
          <FlyToCoordinates latStr={latitude} lngStr={longitude} />
          {parsed && (
            <Marker
              position={[parsed.lat, parsed.lng]}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const p = e.target.getLatLng();
                  handlePick(p.lat, p.lng);
                },
              }}
            />
          )}
        </MapContainer>
      </div>
    </div>
  );
}
